import React from "react"
import PropTypes from "prop-types"
import { trackEvent } from "../../utils/analytics"
import "../../styles/telemetry-landing.css"

const ArrowIcon = () => (
    <svg viewBox="0 0 24 24" aria-hidden="true">
        <path d="M5 12h13M13 6l6 6-6 6" />
    </svg>
)

const TraceGraphic = ({ provider }) => (
    <div className="telemetry-proof__visual" aria-hidden="true">
        <div className="telemetry-proof__toolbar">
            <span />
            <span />
            <span />
            <b>{provider} coaching report</b>
        </div>
        <svg viewBox="0 0 800 360" preserveAspectRatio="none">
            <defs>
                <pattern
                    id={`telemetry-grid-${provider}`}
                    width="48"
                    height="48"
                    patternUnits="userSpaceOnUse"
                >
                    <path d="M48 0H0V48" className="telemetry-grid-line" />
                </pattern>
            </defs>
            <rect
                width="800"
                height="360"
                fill={`url(#telemetry-grid-${provider})`}
            />
            <path
                className="telemetry-trace telemetry-trace--muted"
                d="M0 240C85 238 92 92 178 101s103 146 194 126 95-163 180-144 108 190 248 70"
            />
            <path
                className="telemetry-trace telemetry-trace--speed"
                d="M0 238C85 236 96 117 178 121s98 128 194 107 98-139 180-121 111 163 248 46"
            />
            <path
                className="telemetry-trace telemetry-trace--brake"
                d="M0 286h91v-54h58v54h173v-79h66v79h171v-61h54v61h187"
            />
        </svg>
        <div className="telemetry-proof__legend">
            <span>
                <i className="speed" /> Racing line
            </span>
            <span>
                <i className="brake" /> Braking
            </span>
            <span>
                <i className="throttle" /> Throttle
            </span>
        </div>
    </div>
)

const TelemetryLandingPage = ({
    provider,
    providerKey,
    eyebrow,
    title,
    accentTitle,
    lede,
    sampleHref,
    sampleTitle,
    sampleDescription,
    sampleMetrics,
    sampleHighlights,
    analysisExample,
    importTitle,
    importDescription,
    importButtonLabel,
    onOpenImport,
    importExperience,
    requirements,
    steps,
    features,
    faqs,
}) => {
    const trackImportClick = (placement) => {
        trackEvent("telemetry_cta_click", {
            product: providerKey,
            placement,
        })
        onOpenImport()
    }
    const trackAnalysisOpen = (placement) => {
        trackEvent("telemetry_analysis_open", {
            product: providerKey,
            placement,
        })
    }

    return (
    <main
        className={`telemetry-landing telemetry-landing--${providerKey} row-full`}
    >
        <section className="telemetry-hero">
            <div className="telemetry-shell telemetry-hero__grid">
                <div className="telemetry-hero__copy">
                    <p className="telemetry-kicker">{eyebrow}</p>
                    <h1>
                        {title}
                        <span>{accentTitle}</span>
                    </h1>
                    <p className="telemetry-hero__lede">{lede}</p>
                    <div className="telemetry-actions">
                        <button
                            className="telemetry-button telemetry-button--primary"
                            onClick={() => trackImportClick("hero")}
                            type="button"
                        >
                            {importButtonLabel}
                        </button>
                        <a
                            className="telemetry-button telemetry-button--quiet"
                            href={sampleHref}
                            onClick={() => trackAnalysisOpen("hero")}
                        >
                            Explore the Algarve analysis <ArrowIcon />
                        </a>
                    </div>
                    <p className="telemetry-hero__note">
                        Compare the line, inputs and lap sections in one place.
                        Leave with a focused change to test next.
                    </p>
                </div>
                <a
                    className="telemetry-hero__proof"
                    href={sampleHref}
                    onClick={() => trackAnalysisOpen("hero_proof")}
                >
                    <span className="telemetry-hero__proof-label">
                        Analyzer in action
                    </span>
                    <strong>{sampleTitle}</strong>
                    <p>{sampleDescription}</p>
                    <div className="telemetry-mini-metrics">
                        {sampleMetrics.slice(0, 3).map((metric) => (
                            <span key={metric.label}>
                                <b>{metric.value}</b>
                                <small>{metric.label}</small>
                            </span>
                        ))}
                    </div>
                    <span className="telemetry-text-link">
                        Explore the complete analysis <ArrowIcon />
                    </span>
                </a>
            </div>
            <div className="telemetry-racing-stripe" aria-hidden="true" />
        </section>

        <section
            className="telemetry-section telemetry-section--proof"
            id="sample-report"
            aria-labelledby={`${providerKey}-sample-title`}
        >
            <div className="telemetry-shell">
                <div className="telemetry-section-heading">
                    <p className="telemetry-kicker">What the analyzer finds</p>
                    <h2 id={`${providerKey}-sample-title`}>
                        Turn lap data into a faster next decision
                    </h2>
                    <p>
                        See the measured difference, understand what caused it
                        and take one clear experiment back to the next session.
                    </p>
                </div>
                <div className="telemetry-proof">
                    <TraceGraphic provider={providerKey} />
                    <div className="telemetry-proof__content">
                        <p className="telemetry-proof__eyebrow">
                            From the analyzed session
                        </p>
                        <h3>{analysisExample.title}</h3>
                        <p className="telemetry-analysis-summary">
                            {analysisExample.summary}
                        </p>
                        <div className="telemetry-analysis-metrics">
                            {analysisExample.metrics.map((metric) => (
                                <div key={metric.label}>
                                    <strong>{metric.value}</strong>
                                    <span>{metric.label}</span>
                                </div>
                            ))}
                        </div>
                        <div className="telemetry-analysis-action">
                            <span>Next test</span>
                            <p>{analysisExample.action}</p>
                        </div>
                        <a
                            className="telemetry-text-link telemetry-analysis-link"
                            href={sampleHref}
                            onClick={() => trackAnalysisOpen("analysis")}
                        >
                            Explore every analyzed section <ArrowIcon />
                        </a>
                    </div>
                </div>
                <div className="telemetry-metric-strip">
                    {sampleMetrics.map((metric) => (
                        <div key={metric.label}>
                            <strong>{metric.value}</strong>
                            <span>{metric.label}</span>
                        </div>
                    ))}
                </div>
                <div className="telemetry-capabilities">
                    {sampleHighlights.map((highlight, index) => (
                        <div key={highlight}>
                            <span>{String(index + 1).padStart(2, "0")}</span>
                            <p>{highlight}</p>
                        </div>
                    ))}
                </div>
            </div>
        </section>

        <section
            className="telemetry-section telemetry-section--import"
            id="telemetry-import-gate"
            aria-labelledby={`${providerKey}-import-title`}
        >
            <div className="telemetry-shell telemetry-import-grid">
                <div>
                    <p className="telemetry-kicker">Use your own session</p>
                    <h2 id={`${providerKey}-import-title`}>{importTitle}</h2>
                    <p className="telemetry-section-lede">
                        {importDescription}
                    </p>
                    <div className="telemetry-proof-reminder">
                        <span>
                            Want to see the analyzer on a complete session?
                        </span>
                        <a
                            href={sampleHref}
                            onClick={() => trackAnalysisOpen("proof_reminder")}
                        >
                            Explore the Algarve analysis <ArrowIcon />
                        </a>
                    </div>
                    <div className="telemetry-import-slot">
                        {importExperience}
                    </div>
                    <p className="telemetry-paywall-note">
                        Paddock Pass is required from the first import. Payment
                        starts only after you choose to subscribe.
                    </p>
                </div>
                <aside className="telemetry-requirements">
                    <p className="telemetry-requirements__label">
                        Before you begin
                    </p>
                    <h3>What you need</h3>
                    <ul>
                        {requirements.map((requirement) => (
                            <li key={requirement}>{requirement}</li>
                        ))}
                    </ul>
                </aside>
            </div>
        </section>

        <section
            className="telemetry-section telemetry-section--process"
            id="how-it-works"
        >
            <div className="telemetry-shell">
                <div className="telemetry-section-heading">
                    <p className="telemetry-kicker">Simple workflow</p>
                    <h2>From session file to useful evidence</h2>
                </div>
                <ol className="telemetry-steps">
                    {steps.map((step, index) => (
                        <li key={step.title}>
                            <span>{String(index + 1).padStart(2, "0")}</span>
                            <h3>{step.title}</h3>
                            <p>{step.description}</p>
                        </li>
                    ))}
                </ol>
            </div>
        </section>

        <section className="telemetry-section telemetry-section--features">
            <div className="telemetry-shell">
                <div className="telemetry-section-heading telemetry-section-heading--left">
                    <p className="telemetry-kicker">Built to make you faster</p>
                    <h2>See the cause, not just the lap time.</h2>
                </div>
                <div className="telemetry-features">
                    {features.map((feature, index) => (
                        <article key={feature.title}>
                            <span>{String(index + 1).padStart(2, "0")}</span>
                            <h3>{feature.title}</h3>
                            <p>{feature.description}</p>
                        </article>
                    ))}
                </div>
            </div>
        </section>

        <section className="telemetry-section telemetry-section--faq">
            <div className="telemetry-shell telemetry-faq-grid">
                <div>
                    <p className="telemetry-kicker">Details</p>
                    <h2>Questions before you import</h2>
                    <a
                        className="telemetry-text-link"
                        href={sampleHref}
                        onClick={() => trackAnalysisOpen("faq")}
                    >
                        Explore the complete Algarve analysis <ArrowIcon />
                    </a>
                </div>
                <div className="telemetry-faqs">
                    {faqs.map((faq) => (
                        <details key={faq.question}>
                            <summary>{faq.question}</summary>
                            <p>{faq.answer}</p>
                        </details>
                    ))}
                </div>
            </div>
        </section>

        <section className="telemetry-final">
            <div className="telemetry-shell telemetry-final__grid">
                <div>
                    <p className="telemetry-kicker">Make the next lap count</p>
                    <h2>Know what to change before you drive again.</h2>
                    <p>
                        Bring in your session and turn the racing line, inputs
                        and section differences into a focused next test.
                    </p>
                </div>
                <div className="telemetry-actions telemetry-actions--end">
                    <button
                        className="telemetry-button telemetry-button--light"
                        onClick={() => trackImportClick("final")}
                        type="button"
                    >
                        {importButtonLabel}
                    </button>
                    <a
                        className="telemetry-button telemetry-button--outline"
                        href={sampleHref}
                        onClick={() => trackAnalysisOpen("final")}
                    >
                        Explore the Algarve analysis <ArrowIcon />
                    </a>
                </div>
            </div>
        </section>
    </main>
    )
}

TelemetryLandingPage.propTypes = {
    provider: PropTypes.string.isRequired,
    providerKey: PropTypes.string.isRequired,
    eyebrow: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    accentTitle: PropTypes.string.isRequired,
    lede: PropTypes.string.isRequired,
    sampleHref: PropTypes.string.isRequired,
    sampleTitle: PropTypes.string.isRequired,
    sampleDescription: PropTypes.string.isRequired,
    sampleMetrics: PropTypes.arrayOf(PropTypes.object).isRequired,
    sampleHighlights: PropTypes.arrayOf(PropTypes.string).isRequired,
    analysisExample: PropTypes.shape({
        title: PropTypes.string.isRequired,
        summary: PropTypes.string.isRequired,
        metrics: PropTypes.arrayOf(PropTypes.object).isRequired,
        action: PropTypes.string.isRequired,
    }).isRequired,
    importTitle: PropTypes.string.isRequired,
    importDescription: PropTypes.string.isRequired,
    importButtonLabel: PropTypes.string.isRequired,
    onOpenImport: PropTypes.func.isRequired,
    importExperience: PropTypes.node.isRequired,
    requirements: PropTypes.arrayOf(PropTypes.string).isRequired,
    steps: PropTypes.arrayOf(PropTypes.object).isRequired,
    features: PropTypes.arrayOf(PropTypes.object).isRequired,
    faqs: PropTypes.arrayOf(PropTypes.object).isRequired,
}

TraceGraphic.propTypes = {
    provider: PropTypes.string.isRequired,
}

export default TelemetryLandingPage
